import React, { useState, useEffect, useMemo } from 'react';
import { Scissors, Copy, Download, Shuffle, Eye, Settings, Hash, Filter, Target } from 'lucide-react';

interface SplitMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface SplitOptions {
  preserveEmpty: boolean;
  trimResults: boolean;
  caseSensitive: boolean;
  useRegex: boolean;
  removeWhitespace: boolean;
  sortResults: boolean;
  removeDuplicates: boolean;
  limitResults: boolean;
  maxResults: number;
  addLineNumbers: boolean;
  showIndices: boolean;
  filterMinLength: number;
  filterMaxLength: number;
}

interface SplitResult {
  originalText: string;
  separator: string;
  parts: string[];
  statistics: SplitStats;
  formattedOutput: string;
}

interface SplitStats {
  totalParts: number;
  nonEmptyParts: number;
  emptyParts: number;
  averageLength: number;
  longestPart: string;
  shortestPart: string;
  uniqueParts: number;
  duplicateParts: number;
  totalCharacters: number;
}

const StringSplitter: React.FC = () => {
  const [input, setInput] = useState('');
  const [separator, setSeparator] = useState(',');
  const [selectedMethod, setSelectedMethod] = useState('delimiter');
  const [result, setResult] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [options, setOptions] = useState<SplitOptions>({
    preserveEmpty: true,
    trimResults: false,
    caseSensitive: true,
    useRegex: false,
    removeWhitespace: false,
    sortResults: false,
    removeDuplicates: false,
    limitResults: false,
    maxResults: 100,
    addLineNumbers: false,
    showIndices: false,
    filterMinLength: 0,
    filterMaxLength: 1000,
  });

  const splitMethods: SplitMethod[] = [
    {
      id: 'delimiter',
      name: 'Delimiter Split',
      description: 'Split by character or string delimiter',
      icon: <Scissors className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'regex',
      name: 'Regular Expression',
      description: 'Split using regex patterns',
      icon: <Filter className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'length',
      name: 'Fixed Length',
      description: 'Split into fixed-length chunks',
      icon: <Hash className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'lines',
      name: 'Line Split',
      description: 'Split by line breaks',
      icon: <Scissors className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'words',
      name: 'Word Split',
      description: 'Split into individual words',
      icon: <Scissors className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'characters',
      name: 'Character Split',
      description: 'Split into individual characters',
      icon: <Hash className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'whitespace',
      name: 'Whitespace Split',
      description: 'Split on any whitespace',
      icon: <Filter className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'punctuation',
      name: 'Punctuation Split',
      description: 'Split on punctuation marks',
      icon: <Target className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'camelCase',
      name: 'CamelCase Split',
      description: 'Split on capital letters',
      icon: <Scissors className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'sentences',
      name: 'Sentence Split',
      description: 'Split into sentences',
      icon: <Filter className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'balanced',
      name: 'Balanced Split',
      description: 'Split while preserving brackets/quotes',
      icon: <Target className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'csv',
      name: 'CSV Split',
      description: 'Smart CSV field splitting',
      icon: <Hash className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const performSplit = (text: string, sep: string, method: string, opts: SplitOptions): SplitResult => {
    if (!text) {
      return {
        originalText: text,
        separator: sep,
        parts: [],
        statistics: {
          totalParts: 0,
          nonEmptyParts: 0,
          emptyParts: 0,
          averageLength: 0,
          longestPart: '',
          shortestPart: '',
          uniqueParts: 0,
          duplicateParts: 0,
          totalCharacters: 0
        },
        formattedOutput: ''
      };
    }

    let parts: string[] = [];

    try {
      setErrorMessage('');

      switch (method) {
        case 'delimiter':
          parts = performDelimiterSplit(text, sep, opts);
          break;
        case 'regex':
          parts = performRegexSplit(text, sep, opts);
          break;
        case 'length':
          parts = performLengthSplit(text, parseInt(sep) || 1, opts);
          break;
        case 'lines':
          parts = performLineSplit(text, opts);
          break;
        case 'words':
          parts = performWordSplit(text, opts);
          break;
        case 'characters':
          parts = performCharacterSplit(text, opts);
          break;
        case 'whitespace':
          parts = performWhitespaceSplit(text, opts);
          break;
        case 'punctuation':
          parts = performPunctuationSplit(text, opts);
          break;
        case 'camelCase':
          parts = performCamelCaseSplit(text, opts);
          break;
        case 'sentences':
          parts = performSentenceSplit(text, opts);
          break;
        case 'balanced':
          parts = performBalancedSplit(text, sep, opts);
          break;
        case 'csv':
          parts = performCSVSplit(text, sep, opts);
          break;
        default:
          parts = performDelimiterSplit(text, sep, opts);
      }

      // Apply post-processing options
      parts = applyPostProcessing(parts, opts);

      const statistics = calculateStatistics(parts, text);
      const formattedOutput = formatOutput(parts, opts);

      return {
        originalText: text,
        separator: sep,
        parts,
        statistics,
        formattedOutput
      };

    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred during splitting');
      return {
        originalText: text,
        separator: sep,
        parts: [],
        statistics: {
          totalParts: 0,
          nonEmptyParts: 0,
          emptyParts: 0,
          averageLength: 0,
          longestPart: '',
          shortestPart: '',
          uniqueParts: 0,
          duplicateParts: 0,
          totalCharacters: 0
        },
        formattedOutput: ''
      };
    }
  };

  const performDelimiterSplit = (text: string, delimiter: string, opts: SplitOptions): string[] => {
    if (!delimiter) return [text];
    
    if (opts.useRegex) {
      const flags = opts.caseSensitive ? 'g' : 'gi';
      return text.split(new RegExp(delimiter, flags));
    } else {
      return text.split(delimiter);
    }
  };

  const performRegexSplit = (text: string, pattern: string, opts: SplitOptions): string[] => {
    const flags = `g${opts.caseSensitive ? '' : 'i'}`;
    return text.split(new RegExp(pattern, flags));
  };

  const performLengthSplit = (text: string, length: number, opts: SplitOptions): string[] => {
    const parts: string[] = [];
    for (let i = 0; i < text.length; i += length) {
      parts.push(text.slice(i, i + length));
    }
    return parts;
  };

  const performLineSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/\r?\n/);
  };

  const performWordSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/\s+/).filter(word => word || opts.preserveEmpty);
  };

  const performCharacterSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split('');
  };

  const performWhitespaceSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/\s+/).filter(part => part || opts.preserveEmpty);
  };

  const performPunctuationSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/[.,;:!?"\'\-\(\)\[\]{}]+/).filter(part => part || opts.preserveEmpty);
  };

  const performCamelCaseSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/(?=[A-Z])/).filter(part => part || opts.preserveEmpty);
  };

  const performSentenceSplit = (text: string, opts: SplitOptions): string[] => {
    return text.split(/[.!?]+/).filter(part => part.trim() || opts.preserveEmpty);
  };

  const performBalancedSplit = (text: string, delimiter: string, opts: SplitOptions): string[] => {
    const parts: string[] = [];
    let current = '';
    let depth = 0;
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChars = text.slice(i, i + delimiter.length);

      // Handle quotes
      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
      } else if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = '';
      }

      // Handle brackets
      if (!inQuotes) {
        if (char === '(' || char === '[' || char === '{') {
          depth++;
        } else if (char === ')' || char === ']' || char === '}') {
          depth--;
        }
      }

      // Check for delimiter
      if (nextChars === delimiter && depth === 0 && !inQuotes) {
        parts.push(current);
        current = '';
        i += delimiter.length - 1;
      } else {
        current += char;
      }
    }

    if (current || opts.preserveEmpty) {
      parts.push(current);
    }

    return parts;
  };

  const performCSVSplit = (text: string, delimiter: string, opts: SplitOptions): string[] => {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        parts.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    if (current || opts.preserveEmpty) {
      parts.push(current);
    }

    return parts;
  };

  const applyPostProcessing = (parts: string[], opts: SplitOptions): string[] => {
    let processed = [...parts];

    // Filter empty parts
    if (!opts.preserveEmpty) {
      processed = processed.filter(part => part.trim() !== '');
    }

    // Trim results
    if (opts.trimResults) {
      processed = processed.map(part => part.trim());
    }

    // Remove whitespace
    if (opts.removeWhitespace) {
      processed = processed.map(part => part.replace(/\s+/g, ''));
    }

    // Filter by length
    processed = processed.filter(part => 
      part.length >= opts.filterMinLength && part.length <= opts.filterMaxLength
    );

    // Remove duplicates
    if (opts.removeDuplicates) {
      processed = Array.from(new Set(processed));
    }

    // Sort results
    if (opts.sortResults) {
      processed = processed.sort();
    }

    // Limit results
    if (opts.limitResults && opts.maxResults > 0) {
      processed = processed.slice(0, opts.maxResults);
    }

    return processed;
  };

  const calculateStatistics = (parts: string[], originalText: string): SplitStats => {
    const nonEmptyParts = parts.filter(part => part.trim() !== '').length;
    const emptyParts = parts.length - nonEmptyParts;
    
    const lengths = parts.map(part => part.length);
    const averageLength = lengths.length > 0 ? Math.round(lengths.reduce((sum, len) => sum + len, 0) / lengths.length) : 0;
    
    const longestPart = parts.reduce((longest, part) => part.length > longest.length ? part : longest, '');
    const shortestPart = parts.reduce((shortest, part) => part.length < shortest.length ? part : shortest, parts[0] || '');
    
    const uniqueParts = new Set(parts).size;
    const duplicateParts = parts.length - uniqueParts;
    
    const totalCharacters = parts.reduce((sum, part) => sum + part.length, 0);

    return {
      totalParts: parts.length,
      nonEmptyParts,
      emptyParts,
      averageLength,
      longestPart,
      shortestPart,
      uniqueParts,
      duplicateParts,
      totalCharacters
    };
  };

  const formatOutput = (parts: string[], opts: SplitOptions): string => {
    let formatted = parts.map((part, index) => {
      let line = '';
      
      if (opts.addLineNumbers) {
        line += `${index + 1}. `;
      }
      
      if (opts.showIndices) {
        line += `[${index}] `;
      }
      
      line += part;
      
      return line;
    }).join('\n');

    return formatted;
  };

  const splitResult: SplitResult = useMemo(() => {
    return performSplit(input, separator, selectedMethod, options);
  }, [input, separator, selectedMethod, options]);

  useEffect(() => {
    setResult(splitResult.formattedOutput);
  }, [splitResult]);

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const summary = `String Split Analysis:
Method: ${splitMethods.find(m => m.id === selectedMethod)?.name}
Separator: "${separator}"
Total Parts: ${splitResult.statistics.totalParts}
Non-empty Parts: ${splitResult.statistics.nonEmptyParts}
Average Length: ${splitResult.statistics.averageLength}
Unique Parts: ${splitResult.statistics.uniqueParts}

Split Results:
${result}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'string-split-result.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      'apple,banana,cherry,date,elderberry,fig,grape',
      'The quick brown fox jumps over the lazy dog',
      'firstName:John;lastName:Doe;age:30;city:NewYork',
      'CamelCaseStringForSplittingTest',
      'word1 word2 word3\nline2 content\nline3 more content',
      'Hello world! How are you today? I hope you are well.',
      'user@example.com|admin@site.org|contact@company.net',
      'item1\titem2\titem3\titem4\titem5',
      '{"name":"John","age":30,"city":"NYC"}',
      'Part1|Part2|Part3|Part4|Part5|Part6'
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Scissors className="text-indigo-600" />
            Advanced String Splitter
          </h1>
          <p className="text-gray-600">Split text with multiple methods and advanced analysis</p>
        </div>

        {/* Split Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Split Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                {selectedMethod === 'length' ? 'Chunk Length' : 'Separator'}
              </label>
              <input
                type="text"
                value={separator}
                onChange={(e) => setSeparator(e.target.value)}
                placeholder={
                  selectedMethod === 'length' ? 'Enter chunk length...' :
                  selectedMethod === 'regex' ? 'Enter regex pattern...' :
                  selectedMethod === 'lines' || selectedMethod === 'words' || selectedMethod === 'characters' || 
                  selectedMethod === 'whitespace' || selectedMethod === 'punctuation' || selectedMethod === 'camelCase' || 
                  selectedMethod === 'sentences' ? 'Not required for this method' :
                  'Enter separator...'
                }
                disabled={['lines', 'words', 'characters', 'whitespace', 'punctuation', 'camelCase', 'sentences'].includes(selectedMethod)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none disabled:bg-gray-100"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Method: <span className="font-semibold">{splitMethods.find(m => m.id === selectedMethod)?.name}</span></p>
                <p>Parts: <span className="font-semibold">{splitResult.statistics.totalParts}</span></p>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <Target className="text-red-600" size={16} />
              <span className="text-red-700 text-sm">{errorMessage}</span>
            </div>
          )}
        </div>

        {/* Split Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Split Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Processing Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveEmpty}
                    onChange={(e) => setOptions({...options, preserveEmpty: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Preserve Empty Parts</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimResults}
                    onChange={(e) => setOptions({...options, trimResults: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Results</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.useRegex}
                    onChange={(e) => setOptions({...options, useRegex: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Use Regex (for delimiter)</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeWhitespace}
                    onChange={(e) => setOptions({...options, removeWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Whitespace</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Result Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.sortResults}
                    onChange={(e) => setOptions({...options, sortResults: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Sort Results</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeDuplicates}
                    onChange={(e) => setOptions({...options, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.addLineNumbers}
                    onChange={(e) => setOptions({...options, addLineNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Line Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.showIndices}
                    onChange={(e) => setOptions({...options, showIndices: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Show Array Indices</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.limitResults}
                    onChange={(e) => setOptions({...options, limitResults: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Limit Results</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Filters</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Results</label>
                  <input
                    type="number"
                    min="1"
                    value={options.maxResults}
                    onChange={(e) => setOptions({...options, maxResults: parseInt(e.target.value) || 100})}
                    disabled={!options.limitResults}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    min="0"
                    value={options.filterMinLength}
                    onChange={(e) => setOptions({...options, filterMinLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    min="1"
                    value={options.filterMaxLength}
                    onChange={(e) => setOptions({...options, filterMaxLength: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Text Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Input Text</label>
                <div className="text-sm text-gray-500">
                  {input.length} characters
                </div>
              </div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter text to split here..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Split Results</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {splitResult.statistics.totalParts} parts
                  </div>
                  <button
                    onClick={copyToClipboard}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={result}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Split results will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Split Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Scissors className="text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Split Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {splitMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {splitMethods.find(m => m.id === selectedMethod)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'delimiter' && (
                <div>
                  <p>• Split by any character or string delimiter</p>
                  <p>• Examples: "," for CSV, "|" for pipe-separated, ":" for colon-separated</p>
                </div>
              )}
              {selectedMethod === 'regex' && (
                <div>
                  <p>• Use regex patterns: <code className="bg-gray-200 px-1 rounded">\s+</code> for whitespace</p>
                  <p>• Example: <code className="bg-gray-200 px-1 rounded">[,;:]</code> for multiple delimiters</p>
                </div>
              )}
              {selectedMethod === 'length' && (
                <div>
                  <p>• Split into fixed-length chunks</p>
                  <p>• Enter chunk size in the separator field (e.g., "5" for 5-character chunks)</p>
                </div>
              )}
              {selectedMethod === 'lines' && (
                <div>
                  <p>• Split text by line breaks</p>
                  <p>• Handles both Windows (CRLF) and Unix (LF) line endings</p>
                </div>
              )}
              {selectedMethod === 'words' && (
                <div>
                  <p>• Split into individual words</p>
                  <p>• Uses whitespace as word boundaries</p>
                </div>
              )}
              {selectedMethod === 'characters' && (
                <div>
                  <p>• Split into individual characters</p>
                  <p>• Each character becomes a separate part</p>
                </div>
              )}
              {selectedMethod === 'whitespace' && (
                <div>
                  <p>• Split on any whitespace (spaces, tabs, newlines)</p>
                  <p>• Multiple consecutive whitespace treated as one separator</p>
                </div>
              )}
              {selectedMethod === 'punctuation' && (
                <div>
                  <p>• Split on punctuation marks</p>
                  <p>• Includes: . , ; : ! ? " ' - ( ) [ ] { }</p>
                </div>
              )}
              {selectedMethod === 'camelCase' && (
                <div>
                  <p>• Split CamelCase strings at capital letters</p>
                  <p>• "CamelCaseString" becomes ["Camel", "Case", "String"]</p>
                </div>
              )}
              {selectedMethod === 'sentences' && (
                <div>
                  <p>• Split into sentences</p>
                  <p>• Uses periods, exclamation marks, and question marks</p>
                </div>
              )}
              {selectedMethod === 'balanced' && (
                <div>
                  <p>• Split while respecting brackets and quotes</p>
                  <p>• Won't split inside (parentheses) or "quotes"</p>
                </div>
              )}
              {selectedMethod === 'csv' && (
                <div>
                  <p>• Smart CSV field splitting</p>
                  <p>• Handles quoted fields with embedded delimiters</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && input && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Split Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Total Parts</h3>
                <div className="text-2xl font-bold text-blue-900">{splitResult.statistics.totalParts}</div>
                <p className="text-sm text-blue-600">Split results</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Non-Empty</h3>
                <div className="text-2xl font-bold text-green-900">{splitResult.statistics.nonEmptyParts}</div>
                <p className="text-sm text-green-600">With content</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Average Length</h3>
                <div className="text-2xl font-bold text-orange-900">{splitResult.statistics.averageLength}</div>
                <p className="text-sm text-orange-600">Characters per part</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Unique Parts</h3>
                <div className="text-2xl font-bold text-purple-900">{splitResult.statistics.uniqueParts}</div>
                <p className="text-sm text-purple-600">Distinct values</p>
              </div>
            </div>

            {/* Additional Statistics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Empty Parts</h4>
                <div className="text-lg font-bold text-gray-900">{splitResult.statistics.emptyParts}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Duplicate Parts</h4>
                <div className="text-lg font-bold text-gray-900">{splitResult.statistics.duplicateParts}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Total Characters</h4>
                <div className="text-lg font-bold text-gray-900">{splitResult.statistics.totalCharacters}</div>
              </div>
            </div>

            {/* Longest and Shortest Parts */}
            {splitResult.statistics.longestPart && splitResult.statistics.shortestPart && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Part Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-1">
                      Longest Part ({splitResult.statistics.longestPart.length} chars)
                    </h4>
                    <div className="text-sm text-emerald-700 font-mono truncate max-w-full">
                      {splitResult.statistics.longestPart}
                    </div>
                  </div>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-1">
                      Shortest Part ({splitResult.statistics.shortestPart.length} chars)
                    </h4>
                    <div className="text-sm text-teal-700 font-mono truncate max-w-full">
                      {splitResult.statistics.shortestPart || '(empty)'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Preview */}
        {splitResult.parts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Hash className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Results Preview</h2>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {splitResult.parts.slice(0, 50).map((part, index) => (
                <div key={index} className="p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 font-mono min-w-[3rem]">#{index}</span>
                    <span className="text-sm font-mono text-gray-700 truncate max-w-md">
                      {part || '(empty)'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {part.length} chars
                  </div>
                </div>
              ))}
              {splitResult.parts.length > 50 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {splitResult.parts.length - 50} more parts
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StringSplitter;